import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import {
  NewsCatFragment,
  NewsItemFragment,
  NewsItemHeaderSingleFragment,
  NewsTagFragment,
  OrderEnum,
  ResponsiveImageFragment,
} from "../../types.graphql"
import { Button } from "@components/ui/Button"
import dayjs from "dayjs"
import { Popover } from "@components/ui/Popover"
import { Collapsible } from "@components/ui/Collapsible"
import { MultiSelect } from "@components/ui/MultiSelect"
import { Link, useSearchParams } from "eddev/routing"
import { useInfiniteNewsSearch } from "@hooks/queries"
import { Input } from "@components/ui/Input"
import { IconClose, IconSearch } from "@components/ui/Icon"
import { Select } from "@components/ui/Select"
import { cn } from "@utils/tw"
import { Pagination } from "@components/ui/Pagination"
import { useEffect, useState } from "react"
import { NewsTile } from "./NewsTile"

type allParamTypes = "tag"

export const NewsArchive = ({
  items,
  cats,
  tags,
  layout = "archive",
}: {
  items: (NewsItemFragment & NewsItemHeaderSingleFragment)[]
  cats: NewsCatFragment[]
  tags: NewsTagFragment[]
  layout?: "archive" | "index"
}) => {
  const [page, setPage] = useState(0)
  const [searchParams, searchFilter] = useSearchParams({
    search: "",
    category: "",
    year: [] as string[],
    tag: [] as string[],
    author: [] as string[],
    page,
    sort: OrderEnum.Desc,
  })

  const allCats = cats.map((t) => ({ label: t.name!, value: t.slug! }))
  const allTags = tags.map((t) => ({ label: t.name!, value: t.slug! }))
  const catItems = Array.from(new Set(items.map((i) => i.newsHeader?.category?.slug)).values())
    .filter((i) => i && i !== "")
    .map((y) => allCats.find((c) => c.value === y)!)
  const tagItems = Array.from(new Set(items.map((i) => i.newsHeader?.tags?.map((t) => t?.slug)).flat()))
    .filter((i) => i && i !== "")
    .map((y) => allTags.find((c) => c.value === y)!)
  const yearItems = Array.from(new Set(items.map((i) => dayjs(i.date).year().toString())).values()).map((y) => ({
    label: y,
    value: y,
  }))
  const authorItems = Array.from(new Set(items.map((i) => i.newsHeader?.author?.map((a) => a?.name)).flat())).map(
    (a) => ({ label: a!, value: a! }),
  )

  const allSearches = {
    tag: tagItems,
    year: yearItems,
    author: authorItems,
    category: catItems,
  }

  const allSelected = [
    ...(searchParams.tag || []),
    ...(searchParams.year || []),
    ...(searchParams.author || []),
    ...(searchParams.category !== "" ? [searchParams.category] : []),
  ]

  const shownItems = items.filter((item) => {
    return (
      (!searchParams.search ||
        searchParams.search === "" ||
        item.title?.toLowerCase().includes(searchParams.search.toLowerCase())) &&
      (searchParams.tag.length === 0 ||
        searchParams.tag.every((t) => item.newsHeader?.tags?.some((tag) => tag?.slug === t))) &&
      (searchParams.year.length === 0 || searchParams.year.includes(dayjs(item.date).year().toString())) &&
      (searchParams.author.length === 0 ||
        searchParams.author.some((a) => item.newsHeader?.author?.some((author) => author?.name === a))) &&
      (!searchParams.category ||
        searchParams.category === "" ||
        item.newsHeader?.category?.slug === searchParams.category)
    )
  })

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [searchParams.page])

  const PER_PAGE = 10
  const pagedItems = shownItems.slice(searchParams.page * PER_PAGE, (searchParams.page + 1) * PER_PAGE)

  const indexLayout = layout === "index"

  return (
    <div className="w-full p-4">
      {/* filters bar */}
      <div className="flex flex-col mb-7 gap-3">
        <div className="flex w-full justify-between items-center">
          <div className="flex gap-3 flex-wrap">
            <div className="md:hidden flex">
              <Popover
                modal={false}
                padded
                trigger={<Button variant="secondary">Filter</Button>}
                align="start"
                className="w-[20em] md:w-[25em]"
                arrow={false}
                offsetDistance={5}
              >
                <Collapsible
                  trigger={
                    <p className="flex items-center gap-2">
                      Category&nbsp;
                      {searchParams.category?.length! > 0 && <span className="type-label-s">(1)</span>}
                    </p>
                  }
                  content={
                    <MultiSelect
                      type="list"
                      value={searchParams.category !== "" ? [searchParams.category] : []}
                      onValueChange={(v) => searchFilter.setter("category")(v[0] || "")}
                      items={catItems}
                      className="mt-2"
                    />
                  }
                />
                {yearItems.length > 0 && (
                  <Collapsible
                    trigger={
                      <p className="flex items-center gap-2">
                        Year&nbsp;
                        {searchParams.year?.length! > 0 && (
                          <span className="type-label-s">({searchParams.year?.length})</span>
                        )}
                      </p>
                    }
                    content={
                      <MultiSelect
                        value={searchParams.year || []}
                        type="list"
                        onValueChange={searchFilter.setter("year")}
                        items={yearItems}
                        className="mt-2"
                      />
                    }
                  />
                )}
                {tagItems.length > 0 && (
                  <Collapsible
                    trigger={
                      <p className="flex items-center gap-2">
                        Tags&nbsp;
                        {searchParams.tag?.length! > 0 && (
                          <span className="type-label-s">({searchParams.tag?.length})</span>
                        )}
                      </p>
                    }
                    content={
                      <MultiSelect
                        value={searchParams.tag || []}
                        type="list"
                        onValueChange={searchFilter.setter("tag")}
                        items={tagItems}
                        className="mt-2"
                      />
                    }
                  />
                )}
                {/* {authorItems.length > 0 && (
                  <Collapsible
                    trigger={
                      <p className="flex items-center gap-2">
                        Author&nbsp;
                        {searchParams.author?.length! > 0 && (
                          <span className="type-label-s">({searchParams.author?.length})</span>
                        )}
                      </p>
                    }
                    content={
                      <MultiSelect
                        value={searchParams.author || []}
                        type="list"
                        onValueChange={searchFilter.setter("author")}
                        items={authorItems}
                        className="mt-2"
                      />
                    }
                  />
                )} */}
              </Popover>
            </div>
            <div className="hidden md:flex gap-3 flex-wrap items-center">
              <p className="type-label-s-light mr-5">FILTERS:</p>
              <MultiSelect
                className="hidden md:block"
                value={searchParams.category !== "" ? [searchParams.category] : []}
                placeholder="Category"
                onValueChange={(v) => searchFilter.setter("category")(v[0] || "")}
                items={catItems}
              />
              {yearItems.length > 0 && (
                <MultiSelect
                  className="hidden md:block"
                  value={searchParams.year || []}
                  placeholder="Year"
                  onValueChange={searchFilter.setter("year")}
                  items={yearItems}
                />
              )}
              {tagItems.length > 0 && (
                <MultiSelect
                  className="hidden md:block"
                  value={searchParams.tag || []}
                  placeholder="Tags"
                  onValueChange={searchFilter.setter("tag")}
                  items={tagItems}
                />
              )}
              {/* {authorItems.length > 0 && (
                <MultiSelect
                  className="hidden md:block"
                  value={searchParams.author || []}
                  placeholder="Author"
                  onValueChange={searchFilter.setter("author")}
                  items={authorItems}
                />
              )} */}
            </div>
          </div>

          <div className="flex gap-3">
            <Input
              placeholder="Search"
              value={searchParams.search || ""}
              onChange={(e) => searchFilter.setter("search")(e.target.value)}
              variant="outline"
              after={<IconSearch style={{ width: "1.25em", height: "1.25em" }} />}
              className="pr-1"
            />
          </div>
        </div>

        {allSelected.length > 0 && (
          <div className="flex w-full justify-between items-start mt-5">
            <div className="flex gap-3 flex-wrap">
              {allSelected.map((sel) => {
                return (
                  <Button
                    key={sel}
                    size="sm"
                    onClick={() => {
                      Object.keys(allSearches).forEach((t) => {
                        if (allSearches[t as allParamTypes].find((s) => s.value === sel)) {
                          searchFilter.set(
                            t as allParamTypes,
                            searchParams[t as allParamTypes]?.filter?.((v) => v !== sel) || null,
                          )
                        }
                      })
                    }}
                    leftIcon={<IconClose />}
                    className="pl-2 gap-1"
                  >
                    {sel}
                  </Button>
                )
              })}

              <Button
                size="sm"
                variant="ghost"
                leftIcon={<IconClose />}
                className="pl-2 gap-1"
                onClick={() => {
                  searchFilter.setAll({})
                }}
              >
                Clear all
              </Button>
            </div>

            <div className="md:flex gap-3 type-label-s-light hidden">{`Showing ${shownItems.length} results`}</div>
          </div>
        )}
      </div>

      <div className={cn("w-full grid grid-cols-1 gap-4", indexLayout ? "" : "md:grid-cols-4")}>
        {pagedItems.map((item, ix) => (
          <NewsTile key={ix} item={item} indexLayout={indexLayout} />
        ))}
      </div>

      {shownItems.length > PER_PAGE && (
        <div className="w-full flex justify-center items-center mt-7">
          <Pagination
            current={parseInt(searchParams.page.toString())}
            setCurrent={searchFilter.setter("page")}
            total={Math.floor(shownItems.length / PER_PAGE)}
          />
        </div>
      )}
    </div>
  )
}
